import type { FC } from "react";
import { useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import { ManageSectionPagesModal } from "./ManageSectionPagesModal";

import { MANAGE_CONTENT_QUERY_PARAM, MOBILE_OVERLAY_ROUTE_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { GearSettingsIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionIsLinked,
  selectHasManageableSectionContent,
  selectIsEditModeEnabled,
} from "store/selectors";
import { useIsQueryParamTruthy, useQueryParamsActions } from "utils/routing";

export const ManageSectionPagesButton: FC = () => {
  const isMdOrLarger = useIsMdOrLarger();
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const requestedFromLink = useIsQueryParamTruthy(MANAGE_CONTENT_QUERY_PARAM);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const hasManageableContent = useAppSelector(selectHasManageableSectionContent);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);

  const { value: dialogIsShown, setTrue: showDialog, setFalse: hideDialog } = useBoolean(false);
  const canUseManageContent = (isInLinkedSection || isEditModeEnabled) && hasManageableContent;
  // URL-driven opening is intentionally limited to linked sections. Template and
  // unlinked sections should still require the author to enter Edit Mode.
  const shouldOpenFromLink = requestedFromLink && isInLinkedSection && canUseManageContent;

  useEffect(() => {
    if (!isMdOrLarger && (shouldOpenFromLink || dialogIsShown)) {
      setQueryParams({
        [MANAGE_CONTENT_QUERY_PARAM]: null,
        [MOBILE_OVERLAY_ROUTE_QUERY_PARAM]: "manage_pages",
      });
      hideDialog();
    }
  }, [shouldOpenFromLink, dialogIsShown, isMdOrLarger, setQueryParams, hideDialog]);

  const closeDialog = () => {
    hideDialog();
    if (requestedFromLink) deleteQueryParams([MANAGE_CONTENT_QUERY_PARAM]);
  };

  if (!canUseManageContent) {
    return null;
  }

  const onClick = () => {
    if (isMdOrLarger) {
      showDialog();
    } else {
      setQueryParams({ [MOBILE_OVERLAY_ROUTE_QUERY_PARAM]: "manage_pages" });
    }
  };

  return (
    <>
      <LinkButton size="s" title={t("manage_section_pages_modal.title")} onClick={onClick}>
        <GearSettingsIcon />
        {t("manage_section_pages_modal.button")}
      </LinkButton>
      <Dialog
        dialogPanelClassName="w-[80vw] max-w-[1200px] pb-4"
        open={isMdOrLarger && (dialogIsShown || shouldOpenFromLink)}
        title={
          <span className="flex items-center gap-2">
            <GearSettingsIcon className="text-blue" size="m" />
            {t("manage_section_pages_modal.title")}
          </span>
        }
        onClose={closeDialog}
      >
        <ManageSectionPagesModal onClose={closeDialog} />
      </Dialog>
    </>
  );
};
